/**
 * PitchBook Admin API
 * PitchBook Admin OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {Contract} from '../models/Contract';
import type {ContractDetails} from '../models/ContractDetails';
import type {Error} from '../models/Error';
import type {IntegrationContract} from '../models/IntegrationContract';
import type {IntegrationContractCreate} from '../models/IntegrationContractCreate';
import type {IntegrationContractDetailed} from '../models/IntegrationContractDetailed';
import type {IntegrationContractUpdate} from '../models/IntegrationContractUpdate';
import type {PayPerCallContractSave} from '../models/PayPerCallContractSave';
import type {PayPerCallContractUpdate} from '../models/PayPerCallContractUpdate';
import type {SubscriptionContractSave} from '../models/SubscriptionContractSave';
import type {SubscriptionContractUpdate} from '../models/SubscriptionContractUpdate';
import type {SubscriptionPackageInfo} from '../models/SubscriptionPackageInfo';

export interface CreateIntegrationContract$Request {
    accountId: number;
    integrationContractCreate?: IntegrationContractCreate;
}

export interface CreateIntegrationContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const createIntegrationContractRequest = createAction<PayloadData<void, CreateIntegrationContract$Request>>('CONTRACT_CONTROLLER__CREATE_INTEGRATION_CONTRACT__REQUEST');
export const createIntegrationContractSuccess = createAction<PayloadData<void, CreateIntegrationContract$Request>>('CONTRACT_CONTROLLER__CREATE_INTEGRATION_CONTRACT__SUCCESS');
export const createIntegrationContractFailure = createAction<PayloadData<Error, CreateIntegrationContract$Request, true>>('CONTRACT_CONTROLLER__CREATE_INTEGRATION_CONTRACT__FAILURE');

export const createIntegrationContractActions = [
    createIntegrationContractRequest,
    createIntegrationContractSuccess,
    createIntegrationContractFailure
] as const;

/**
 * Contract save
 * @param {number} meta.accountId Id of account
 * @param {IntegrationContractCreate} [meta.integrationContractCreate]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const createIntegrationContract = (meta: CreateIntegrationContract$Request, config?: CreateIntegrationContract$Config) => {
    return {
        CALL_API: {
            types: createIntegrationContractActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/integration`,
            TRANSPORT_REQUEST_BODY: meta.integrationContractCreate,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetContractsBasic$Request {
    accountId: number;
}

export interface GetContractsBasic$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getContractsBasicRequest = createAction<PayloadData<void, GetContractsBasic$Request>>('CONTRACT_CONTROLLER__GET_CONTRACTS_BASIC__REQUEST');
export const getContractsBasicSuccess = createAction<PayloadData<Array<Contract>, GetContractsBasic$Request>>('CONTRACT_CONTROLLER__GET_CONTRACTS_BASIC__SUCCESS');
export const getContractsBasicFailure = createAction<PayloadData<Error, GetContractsBasic$Request, true>>('CONTRACT_CONTROLLER__GET_CONTRACTS_BASIC__FAILURE');

export const getContractsBasicActions = [
    getContractsBasicRequest,
    getContractsBasicSuccess,
    getContractsBasicFailure
] as const;

/**
 * Get all contracts basic info by accountId
 * @param {number} meta.accountId Id of account
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getContractsBasic = (meta: GetContractsBasic$Request, config?: GetContractsBasic$Config) => {
    return {
        CALL_API: {
            types: getContractsBasicActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/basic`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetContractsDetails$Request {
    accountId: number;
}

export interface GetContractsDetails$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getContractsDetailsRequest = createAction<PayloadData<void, GetContractsDetails$Request>>('CONTRACT_CONTROLLER__GET_CONTRACTS_DETAILS__REQUEST');
export const getContractsDetailsSuccess = createAction<PayloadData<Array<ContractDetails>, GetContractsDetails$Request>>('CONTRACT_CONTROLLER__GET_CONTRACTS_DETAILS__SUCCESS');
export const getContractsDetailsFailure = createAction<PayloadData<Error, GetContractsDetails$Request, true>>('CONTRACT_CONTROLLER__GET_CONTRACTS_DETAILS__FAILURE');

export const getContractsDetailsActions = [
    getContractsDetailsRequest,
    getContractsDetailsSuccess,
    getContractsDetailsFailure
] as const;

/**
 * Get all contracts by accountId
 * @param {number} meta.accountId Id of account
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getContractsDetails = (meta: GetContractsDetails$Request, config?: GetContractsDetails$Config) => {
    return {
        CALL_API: {
            types: getContractsDetailsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/details`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetIntegrationContracts$Request {
    accountId: number;
}

export interface GetIntegrationContracts$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getIntegrationContractsRequest = createAction<PayloadData<void, GetIntegrationContracts$Request>>('CONTRACT_CONTROLLER__GET_INTEGRATION_CONTRACTS__REQUEST');
export const getIntegrationContractsSuccess = createAction<PayloadData<Array<IntegrationContractDetailed>, GetIntegrationContracts$Request>>('CONTRACT_CONTROLLER__GET_INTEGRATION_CONTRACTS__SUCCESS');
export const getIntegrationContractsFailure = createAction<PayloadData<Error, GetIntegrationContracts$Request, true>>('CONTRACT_CONTROLLER__GET_INTEGRATION_CONTRACTS__FAILURE');

export const getIntegrationContractsActions = [
    getIntegrationContractsRequest,
    getIntegrationContractsSuccess,
    getIntegrationContractsFailure
] as const;

/**
 * Get integration contracts by accountId
 * @param {number} meta.accountId Id of account
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getIntegrationContracts = (meta: GetIntegrationContracts$Request, config?: GetIntegrationContracts$Config) => {
    return {
        CALL_API: {
            types: getIntegrationContractsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/integration/contracts`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetSubscriptionPackages$Request {
    accountId: number;
}

export interface GetSubscriptionPackages$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getSubscriptionPackagesRequest = createAction<PayloadData<void, GetSubscriptionPackages$Request>>('CONTRACT_CONTROLLER__GET_SUBSCRIPTION_PACKAGES__REQUEST');
export const getSubscriptionPackagesSuccess = createAction<PayloadData<Array<SubscriptionPackageInfo>, GetSubscriptionPackages$Request>>('CONTRACT_CONTROLLER__GET_SUBSCRIPTION_PACKAGES__SUCCESS');
export const getSubscriptionPackagesFailure = createAction<PayloadData<Error, GetSubscriptionPackages$Request, true>>('CONTRACT_CONTROLLER__GET_SUBSCRIPTION_PACKAGES__FAILURE');

export const getSubscriptionPackagesActions = [
    getSubscriptionPackagesRequest,
    getSubscriptionPackagesSuccess,
    getSubscriptionPackagesFailure
] as const;

/**
 * Get subscription packages
 * @param {number} meta.accountId Id of account
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getSubscriptionPackages = (meta: GetSubscriptionPackages$Request, config?: GetSubscriptionPackages$Config) => {
    return {
        CALL_API: {
            types: getSubscriptionPackagesActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/subscription-packages`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface SavePayPerCallContract$Request {
    accountId: number;
    payPerCallContractSave?: PayPerCallContractSave;
}

export interface SavePayPerCallContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const savePayPerCallContractRequest = createAction<PayloadData<void, SavePayPerCallContract$Request>>('CONTRACT_CONTROLLER__SAVE_PAY_PER_CALL_CONTRACT__REQUEST');
export const savePayPerCallContractSuccess = createAction<PayloadData<void, SavePayPerCallContract$Request>>('CONTRACT_CONTROLLER__SAVE_PAY_PER_CALL_CONTRACT__SUCCESS');
export const savePayPerCallContractFailure = createAction<PayloadData<Error, SavePayPerCallContract$Request, true>>('CONTRACT_CONTROLLER__SAVE_PAY_PER_CALL_CONTRACT__FAILURE');

export const savePayPerCallContractActions = [
    savePayPerCallContractRequest,
    savePayPerCallContractSuccess,
    savePayPerCallContractFailure
] as const;

/**
 * Contract save
 * @param {number} meta.accountId Id of account
 * @param {PayPerCallContractSave} [meta.payPerCallContractSave]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const savePayPerCallContract = (meta: SavePayPerCallContract$Request, config?: SavePayPerCallContract$Config) => {
    return {
        CALL_API: {
            types: savePayPerCallContractActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/pay-per-call/save`,
            TRANSPORT_REQUEST_BODY: meta.payPerCallContractSave,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface SaveSubscriptionContract$Request {
    accountId: number;
    subscriptionContractSave?: SubscriptionContractSave;
}

export interface SaveSubscriptionContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const saveSubscriptionContractRequest = createAction<PayloadData<void, SaveSubscriptionContract$Request>>('CONTRACT_CONTROLLER__SAVE_SUBSCRIPTION_CONTRACT__REQUEST');
export const saveSubscriptionContractSuccess = createAction<PayloadData<void, SaveSubscriptionContract$Request>>('CONTRACT_CONTROLLER__SAVE_SUBSCRIPTION_CONTRACT__SUCCESS');
export const saveSubscriptionContractFailure = createAction<PayloadData<Error, SaveSubscriptionContract$Request, true>>('CONTRACT_CONTROLLER__SAVE_SUBSCRIPTION_CONTRACT__FAILURE');

export const saveSubscriptionContractActions = [
    saveSubscriptionContractRequest,
    saveSubscriptionContractSuccess,
    saveSubscriptionContractFailure
] as const;

/**
 * Contract save
 * @param {number} meta.accountId Id of account
 * @param {SubscriptionContractSave} [meta.subscriptionContractSave]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const saveSubscriptionContract = (meta: SaveSubscriptionContract$Request, config?: SaveSubscriptionContract$Config) => {
    return {
        CALL_API: {
            types: saveSubscriptionContractActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/subscription/save`,
            TRANSPORT_REQUEST_BODY: meta.subscriptionContractSave,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateIntegrationContract$Request {
    accountId: number;
    integrationContractUpdate?: IntegrationContractUpdate;
}

export interface UpdateIntegrationContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateIntegrationContractRequest = createAction<PayloadData<void, UpdateIntegrationContract$Request>>('CONTRACT_CONTROLLER__UPDATE_INTEGRATION_CONTRACT__REQUEST');
export const updateIntegrationContractSuccess = createAction<PayloadData<IntegrationContract, UpdateIntegrationContract$Request>>('CONTRACT_CONTROLLER__UPDATE_INTEGRATION_CONTRACT__SUCCESS');
export const updateIntegrationContractFailure = createAction<PayloadData<Error, UpdateIntegrationContract$Request, true>>('CONTRACT_CONTROLLER__UPDATE_INTEGRATION_CONTRACT__FAILURE');

export const updateIntegrationContractActions = [
    updateIntegrationContractRequest,
    updateIntegrationContractSuccess,
    updateIntegrationContractFailure
] as const;

/**
 * Update CRM Integration contract
 * @param {number} meta.accountId Id of Account
 * @param {IntegrationContractUpdate} [meta.integrationContractUpdate]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateIntegrationContract = (meta: UpdateIntegrationContract$Request, config?: UpdateIntegrationContract$Config) => {
    return {
        CALL_API: {
            types: updateIntegrationContractActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/integration/update`,
            TRANSPORT_REQUEST_BODY: meta.integrationContractUpdate,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdatePayPerCallContract$Request {
    accountId: number;
    payPerCallContractUpdate?: PayPerCallContractUpdate;
}

export interface UpdatePayPerCallContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updatePayPerCallContractRequest = createAction<PayloadData<void, UpdatePayPerCallContract$Request>>('CONTRACT_CONTROLLER__UPDATE_PAY_PER_CALL_CONTRACT__REQUEST');
export const updatePayPerCallContractSuccess = createAction<PayloadData<Contract, UpdatePayPerCallContract$Request>>('CONTRACT_CONTROLLER__UPDATE_PAY_PER_CALL_CONTRACT__SUCCESS');
export const updatePayPerCallContractFailure = createAction<PayloadData<Error, UpdatePayPerCallContract$Request, true>>('CONTRACT_CONTROLLER__UPDATE_PAY_PER_CALL_CONTRACT__FAILURE');

export const updatePayPerCallContractActions = [
    updatePayPerCallContractRequest,
    updatePayPerCallContractSuccess,
    updatePayPerCallContractFailure
] as const;

/**
 * Update Pay Per Call contract
 * @param {number} meta.accountId Id of Account
 * @param {PayPerCallContractUpdate} [meta.payPerCallContractUpdate]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updatePayPerCallContract = (meta: UpdatePayPerCallContract$Request, config?: UpdatePayPerCallContract$Config) => {
    return {
        CALL_API: {
            types: updatePayPerCallContractActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/pay-per-call/update`,
            TRANSPORT_REQUEST_BODY: meta.payPerCallContractUpdate,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateSubscriptionContract$Request {
    accountId: number;
    subscriptionContractUpdate?: SubscriptionContractUpdate;
}

export interface UpdateSubscriptionContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateSubscriptionContractRequest = createAction<PayloadData<void, UpdateSubscriptionContract$Request>>('CONTRACT_CONTROLLER__UPDATE_SUBSCRIPTION_CONTRACT__REQUEST');
export const updateSubscriptionContractSuccess = createAction<PayloadData<Contract, UpdateSubscriptionContract$Request>>('CONTRACT_CONTROLLER__UPDATE_SUBSCRIPTION_CONTRACT__SUCCESS');
export const updateSubscriptionContractFailure = createAction<PayloadData<Error, UpdateSubscriptionContract$Request, true>>('CONTRACT_CONTROLLER__UPDATE_SUBSCRIPTION_CONTRACT__FAILURE');

export const updateSubscriptionContractActions = [
    updateSubscriptionContractRequest,
    updateSubscriptionContractSuccess,
    updateSubscriptionContractFailure
] as const;

/**
 * Contract updated
 * @param {number} meta.accountId Id of Account
 * @param {SubscriptionContractUpdate} [meta.subscriptionContractUpdate]
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateSubscriptionContract = (meta: UpdateSubscriptionContract$Request, config?: UpdateSubscriptionContract$Config) => {
    return {
        CALL_API: {
            types: updateSubscriptionContractActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/api-v2/${meta.accountId}/contracts/subscription/update`,
            TRANSPORT_REQUEST_BODY: meta.subscriptionContractUpdate,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

